/** Every read the console makes, as TanStack Query hooks with shared keys.
 *
 *  Screens read through these instead of useEffect + useState, so a write in
 *  one place (a post scheduled, a request sent) refreshes every screen showing
 *  it: a mutation invalidates the keys below (lib/postActions.ts for posts).
 *  Keys that the pages not yet moved also use (Dashboard) are kept identical
 *  so they share the cache. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type RedeemCodeState, type ScheduleQuery } from '@/api/client'

export { useJobRuns } from '@/admin-design/_common/jobs/api'

/** The live screens (today, schedule, the attention strip) poll this often. */
export const LIVE_MS = 30_000

export const qk = {
  me: ['me'] as const,
  version: ['version'] as const,
  deskHealth: ['deskHealth'] as const,
  deskStats: (days: number) => ['deskStats', days] as const,
  draftCounts: (opts: DraftFilter = {}) => ['draftCounts', opts] as const,
  drafts: (status: string, opts: DraftFilter = {}) => ['drafts', status, opts] as const,
  campaigns: ['campaigns'] as const,
  campaign: (id: number) => ['campaign', id] as const,
  redeemApps: ['redeemApps'] as const,
  redeemApp: (id: number) => ['redeemApp', id] as const,
  redeemCodes: (appId: number, state: string, limit: number) => ['redeemCodes', appId, state, limit] as const,
  deskControls: ['deskControls'] as const,
  redeemPosts: (appId: number, status: string) => ['redeemPosts', appId, status] as const,
  requests: (state: string, appId: number, limit: number) => ['requests', state, appId, limit] as const,
  targets: ['targets'] as const,
  affiliateOverview: ['affiliateOverview'] as const,
  autoApproveLastRun: ['autoApproveLastRun'] as const,
  affiliateCategories: ['affiliateCategories'] as const,
  affiliateAvatars: ['affiliateAvatars'] as const,
  media: ['media'] as const,
  bluesky: ['bluesky'] as const,
  schedule: (q: ScheduleQuery) => ['schedule', q] as const,
  redeemPost: (id: number) => ['redeemPost', id] as const,
  draft: (id: number) => ['draft', id] as const,
  publicCodes: (postId: number) => ['publicCodes', postId] as const,
}

export interface DraftFilter {
  campaignId?: number
  platform?: string
  subreddit?: string
}

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: api.me, staleTime: 5 * 60_000 })
}

/** The build the server runs, for the sidebar footer. */
export function useVersion() {
  return useQuery({ queryKey: qk.version, queryFn: api.version, staleTime: Infinity })
}

/** Is the desk still reading, handing out and posting. Feeds the attention strip. */
export function useDeskHealth() {
  return useQuery({ queryKey: qk.deskHealth, queryFn: api.redeemHealth, refetchInterval: LIVE_MS })
}

export function useDeskStats(days = 7) {
  return useQuery({ queryKey: qk.deskStats(days), queryFn: () => api.redeemStats(days) })
}

/** Draft counts by status — the review badge reads pending_review from it. */
export function useDraftCounts(opts: DraftFilter = {}, live = false) {
  return useQuery({
    queryKey: qk.draftCounts(opts),
    queryFn: () => api.draftCounts(opts),
    refetchInterval: live ? LIVE_MS : undefined,
  })
}

export function useDrafts(status = 'pending_review', opts: DraftFilter = {}, live = false) {
  return useQuery({
    queryKey: qk.drafts(status, opts),
    queryFn: () => api.drafts(status, opts),
    refetchInterval: live ? LIVE_MS : undefined,
  })
}

export function useCampaigns() {
  return useQuery({ queryKey: qk.campaigns, queryFn: api.campaigns })
}

/** The Coupang auto-approve tick's last start, for the "N분 뒤 자동 승인" note. */
export function useAutoApproveLastRun() {
  return useQuery({ queryKey: qk.autoApproveLastRun, queryFn: api.autoApproveLastRun, refetchInterval: LIVE_MS })
}

export function useCampaign(id: number) {
  return useQuery({ queryKey: qk.campaign(id), queryFn: () => api.campaign(id), enabled: id > 0 })
}

export function useRedeemApps() {
  return useQuery({ queryKey: qk.redeemApps, queryFn: () => api.redeemApps() })
}

export function useRedeemApp(id: number) {
  return useQuery({ queryKey: qk.redeemApp(id), queryFn: () => api.redeemApp(id), enabled: id > 0 })
}

export function useRedeemPosts(appId = 0, status = '') {
  return useQuery({ queryKey: qk.redeemPosts(appId, status), queryFn: () => api.redeemPosts(appId, status) })
}

export function useRequests(state = '', appId = 0, limit = 100, live = false) {
  return useQuery({
    queryKey: qk.requests(state, appId, limit),
    queryFn: () => api.redeemRequests(state, appId, limit),
    refetchInterval: live ? LIVE_MS : undefined,
    placeholderData: (prev) => prev,
  })
}

/** One app's codes, newest first; the server caps a page at 500. */
export function useRedeemCodes(appId: number, state: RedeemCodeState | '' = '', limit = 500) {
  return useQuery({
    queryKey: qk.redeemCodes(appId, state, limit),
    queryFn: () => api.redeemCodes(appId, { state, limit }),
    enabled: appId > 0,
    placeholderData: (prev) => prev,
  })
}

/** The desk's pause switch and its blocklist. */
export function useDeskControls() {
  return useQuery({ queryKey: qk.deskControls, queryFn: api.redeemControls })
}

export function useTargets() {
  return useQuery({ queryKey: qk.targets, queryFn: api.targets })
}

export function useMedia() {
  return useQuery({ queryKey: qk.media, queryFn: api.media })
}

/** Uploads files one after another; the library is read again when done. */
export function useUploadMedia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (files: File[]) => {
      const done = []
      for (const f of files) done.push(await api.uploadMedia(f))
      return done
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.media }),
  })
}

export function useDeleteMedia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteMedia(id),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.media }),
  })
}

export function useBlueskySettings() {
  return useQuery({ queryKey: qk.bluesky, queryFn: api.blueskySettings })
}

/** The unified schedule. Live screens poll it; the key carries the whole
 *  query, and every post mutation invalidates ['schedule'] as a prefix. */
export function useSchedule(q: ScheduleQuery, opts: { live?: boolean; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: qk.schedule(q),
    queryFn: () => api.schedule(q),
    refetchInterval: opts.live ? LIVE_MS : undefined,
    enabled: opts.enabled ?? true,
    // Paging a week or a month keeps the last answer on screen until the
    // next arrives, instead of flashing an empty grid.
    placeholderData: (prev) => prev,
  })
}

export function useRedeemPost(id: number) {
  return useQuery({ queryKey: qk.redeemPost(id), queryFn: () => api.redeemPost(id), enabled: id > 0 })
}

export function useDraft(id: number) {
  return useQuery({ queryKey: qk.draft(id), queryFn: () => api.draft(id), enabled: id > 0 })
}

/** A manual post's public codes, numbered in the order people comment against. */
export function usePublicCodes(postId: number, enabled = true) {
  return useQuery({
    queryKey: qk.publicCodes(postId),
    queryFn: () => api.redeemPublicCodes(postId),
    enabled: enabled && postId > 0,
  })
}

/** The Coupang page's tiles. */
export function useAffiliateOverview() {
  return useQuery({ queryKey: qk.affiliateOverview, queryFn: () => api.affiliateOverview(), refetchInterval: LIVE_MS })
}

export function useAffiliateCategories() {
  return useQuery({ queryKey: qk.affiliateCategories, queryFn: () => api.affiliateCategories() })
}

/** The persona catalog a Coupang campaign picks a voice from. */
export function useAffiliateAvatars() {
  return useQuery({ queryKey: qk.affiliateAvatars, queryFn: () => api.affiliateAvatars(), staleTime: Infinity })
}
